import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth';
import { jwtDecode } from 'jwt-decode';
import api from '../api';
import AuthVisual from '../AuthVisual';
import LanguageSwitcher from '../LanguageSwitcher';
import './Login.css';

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setMessage('');
    setIsLoading(true);

    try {
      const response = await api.post('/login', {
        email,
        passwd: password,
      });

      const data = response.data;
      const { token } = data;

      if (!token || typeof token !== 'string') {
        setMessage(t('login.authError'));
        setIsLoading(false);
        return;
      }

      try {
        const decodedToken = jwtDecode(token);

        if (String(decodedToken.status) !== '1') {
          setMessage(t('login.disabled'));
          setIsLoading(false);
          return;
        }

        if (!login(token)) {
          setMessage(t('login.authError'));
          setIsLoading(false);
          return;
        }

        navigate(
          decodedToken.owner === 'admin'
            ? '/dashboard'
            : '/dashboard-cliente'
        );
      } catch (decodeError) {
        console.error('Erro ao decodificar token:', decodeError);
        setMessage(t('login.processingError'));
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Erro no login:', error);

      const msg = i18n.resolvedLanguage === 'en'
        ? t('login.requestError')
        : error.response?.data?.message || t('login.requestError');

      setMessage(msg);
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <LanguageSwitcher className="auth-language-switcher" />
      <AuthVisual className="login-visual" />

      <div className="login-container">
        <h1>{t('login.title')}</h1>
        <p>{t('login.subtitle')}</p>

        <form
          className="login-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleLogin();
          }}
        >
          <input
            type="email"
            placeholder={t('login.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            aria-label={t('login.email')}
          />
          <input
            type="password"
            placeholder={t('login.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            aria-label={t('login.password')}
          />
          <button type="submit" disabled={isLoading}>
            {isLoading ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        {message && (
          <p className="message error" role="alert">
            {message}
          </p>
        )}

        <div className="login-links">
          <Link to="/cadastro" className="register-button">
            {t('login.register')}
          </Link>
          <Link to="/" className="back-button">
            {t('common.back')}
          </Link>
        </div>

        <h4 className="login-help-title">
          {t('login.help')}
        </h4>

        <div className="login-support-links">
          <a
            href="https://wa.me/5542984241307"
            target="_blank"
            rel="noopener noreferrer"
            className="login-support-link whatsapp"
          >
            <span role="img" aria-label="WhatsApp">
              W
            </span>
            (42) 98424-1307 - WhatsApp
          </a>

          <a
            href="mailto:suporte@quantumtrades.com.br"
            className="login-support-link email"
          >
            <span role="img" aria-label="Email">
              @
            </span>
            suporte@quantumtrades.com.br
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
